/**
 * Canonical Token Registry
 *
 * The single source of truth for which CSS custom properties must exist
 * in a valid theme.css file, along with their expected types and descriptions.
 *
 * @module
 */
import type { TokenDef } from "./types"

export const CANONICAL_TOKENS: readonly TokenDef[] = [
  // ── Brand ──
  { name: "--color-brand",      type: "css-color",  description: "Primary brand color",           section: "brand" },
  { name: "--color-brand-fg",   type: "css-color",  description: "Text on brand backgrounds",     section: "brand" },
  { name: "--color-brand-tint", type: "css-color",  description: "Light brand surface",           section: "brand" },
  // ── Page ──
  { name: "--color-bg",         type: "css-color",  description: "Page background",               section: "page" },
  { name: "--color-surface",    type: "css-color",  description: "Card/panel background",         section: "page" },
  { name: "--color-border",     type: "css-color",  description: "Borders and dividers",          section: "page" },
  // ── Text ──
  { name: "--color-text",       type: "css-color",  description: "Primary text",                  section: "text" },
  { name: "--color-text-soft",  type: "css-color",  description: "Secondary text",                section: "text" },
  // ── Fonts ──
  { name: "--font-heading",     type: "font-provider-var", description: "Heading typeface",              section: "fonts" },
  { name: "--font-body",        type: "font-provider-var", description: "Body typeface",                 section: "fonts" },
  // ── Type Scale ──
  { name: "--text-sm",          type: "css-length", description: "Small text size (14px)",        section: "type-scale" },
  { name: "--text-base",        type: "css-length", description: "Base text size (16px)",         section: "type-scale" },
  { name: "--text-lg",          type: "css-length", description: "Large text size (20px)",        section: "type-scale" },
  { name: "--text-xl",          type: "css-length", description: "Subheading size (26px)",        section: "type-scale" },
  { name: "--text-2xl",         type: "css-length", description: "Section heading size (34px)",   section: "type-scale" },
  { name: "--text-3xl",         type: "css-length", description: "Page heading size (46px)",      section: "type-scale" },
  { name: "--text-hero",        type: "css-length", description: "Hero headline size (60px)",     section: "type-scale" },
  // ── Radius ──
  { name: "--radius-sm",        type: "css-length", description: "Small radius (6px)",            section: "radius" },
  { name: "--radius-md",        type: "css-length", description: "Default radius (10px)",         section: "radius" },
  { name: "--radius-lg",        type: "css-length", description: "Large radius (16px)",           section: "radius" },
  // ── Shadow ──
  { name: "--shadow-sm",        type: "css-shadow", description: "Subtle elevation",              section: "shadow" },
  { name: "--shadow-md",        type: "css-shadow", description: "Card elevation",                section: "shadow" },
] as const

export const CANONICAL_MAP = new Map(CANONICAL_TOKENS.map((t) => [t.name, t]))

export const SECTION_LABELS: Record<string, string> = {
  "brand": "Brand colors",
  "page": "Page colors",
  "text": "Text colors",
  "fonts": "Fonts",
  "type-scale": "Type scale",
  "radius": "Border radius",
  "shadow": "Shadows",
}
